import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { postJson } from "@/lib/api";
import { getAttribution } from "@/lib/attribution";
import { trackLead } from "@/lib/tracking";

const THEMES = {
  light: { input: "bg-white text-navy ring-1 ring-navy/15 placeholder:text-navy/60", text: "text-navy/80", link: "text-teal-dark", btn: "bg-teal text-white hover:bg-teal-dark", box: "accent-[#0CA6A2]" },
  dark: { input: "bg-white text-navy placeholder:text-navy/60", text: "text-white/90", link: "text-white", btn: "bg-navy text-cream hover:bg-[#0c2644]", box: "accent-[#12345A]" },
};

const Success = ({ theme }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} role="status" data-testid="early-access-success" className={`flex items-start gap-3 rounded-2xl p-5 ${theme === "dark" ? "bg-white/15 text-white" : "bg-teal/10 text-navy"}`}>
    <CheckCircle2 className={`mt-0.5 h-6 w-6 shrink-0 ${theme === "dark" ? "text-white" : "text-teal-dark"}`} aria-hidden="true" />
    <div>
      <p className="font-heading text-lg font-extrabold">You're on the early-access list!</p>
      <p className="mt-1 text-sm leading-relaxed opacity-90">We'll email you when KeepThis! is ready. Thank you for helping us make room for more memories.</p>
    </div>
  </motion.div>
);

export const EarlyAccessForm = ({ source, theme = "light", withName = false, autoFocus = false, onSuccess }) => {
  const t = THEMES[theme];
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const id = `ea-${source}`;

  const submit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) return setError("Please enter a valid email address.");
    if (!consent) return setError("Please tick the box so we can email you about early access.");
    setStatus("loading");
    setError("");
    try {
      await postJson("/early-access", { email: email.trim(), first_name: name.trim() || null, consent_marketing: consent, website, source, ...getAttribution() });
      trackLead(source);
      setStatus("done");
      onSuccess?.();
    } catch (err) {
      setError(/check what you entered/i.test(err.message) ? "Please enter a valid email address." : err.message);
      setStatus("idle");
    }
  };

  if (status === "done") return <Success theme={theme} />;

  return (
    <form onSubmit={submit} noValidate data-testid={`early-access-form-${source}`} className="w-full">
      {withName && (
        <div className="mb-3">
          <label htmlFor={`${id}-name`} className={`mb-1.5 block font-heading text-sm font-extrabold ${theme === "dark" ? "text-white" : "text-navy"}`}>First name <span className="font-sans text-xs font-medium opacity-80">(optional)</span></label>
          <input id={`${id}-name`} data-testid={`early-access-name-${source}`} value={name} maxLength={60} autoComplete="given-name" onChange={(e) => setName(e.target.value)} className={`h-14 w-full rounded-full px-5 text-base focus:outline-none focus:ring-4 focus:ring-teal/30 ${t.input}`} />
        </div>
      )}
      <div className="flex flex-col gap-3 sm:flex-row">
        <label htmlFor={`${id}-email`} className="sr-only">Email address</label>
        <input
          id={`${id}-email`}
          data-testid={`early-access-email-${source}`}
          type="email"
          inputMode="email"
          autoComplete="email"
          required
          autoFocus={autoFocus}
          maxLength={254}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email address"
          className={`h-14 w-full rounded-full px-5 text-base transition-[box-shadow] focus:outline-none focus:ring-4 focus:ring-teal/30 sm:flex-1 ${t.input}`}
        />
        <button type="submit" data-testid={`early-access-submit-${source}`} disabled={status === "loading"} className={`group inline-flex h-14 shrink-0 items-center justify-center gap-2 rounded-full px-7 font-heading font-extrabold shadow-[0_14px_34px_-12px_rgba(12,166,162,0.6)] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 disabled:opacity-70 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal/40 ${t.btn}`}>
          {status === "loading" ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
          Get Early Access
          {status !== "loading" && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />}
        </button>
      </div>
      <div className="absolute -left-[9999px]" aria-hidden="true">
        <label htmlFor={`${id}-website`}>Website</label>
        <input id={`${id}-website`} tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
      </div>
      <label htmlFor={`${id}-consent`} className={`mt-4 flex cursor-pointer items-start gap-3 text-sm leading-relaxed ${t.text}`}>
        <input id={`${id}-consent`} data-testid={`early-access-consent-${source}`} type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className={`mt-0.5 h-5 w-5 shrink-0 cursor-pointer rounded ${t.box}`} />
        <span>Yes, email me about KeepThis! early access and launch updates. I can unsubscribe anytime.</span>
      </label>
      <p className={`mt-2 pl-8 text-xs leading-relaxed ${t.text}`}>
        By joining, you agree to our <Link to="/terms" className={`font-semibold underline underline-offset-2 ${t.link}`}>Terms of Service</Link> and <Link to="/privacy" className={`font-semibold underline underline-offset-2 ${t.link}`}>Privacy Policy</Link>.
      </p>
      {error && <p role="alert" data-testid={`early-access-error-${source}`} className={`mt-3 rounded-xl px-4 py-2.5 text-sm font-semibold ${theme === "dark" ? "bg-white text-navy" : "bg-coral/25 text-navy"}`}>{error}</p>}
    </form>
  );
};
